import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { lazy } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import PageTransition from '../components/PageTransition';

import Layout from '../layout';
import CloneLayout from '../layout/cloneLayout';
import EnquireLayout from '../layout/enquireLayout';
import MasterLayout from '../layout/masterLayout';
import DocumentLayout from '../layout/documentLayout';
import PrivateLayout from '../layout/PrivateLayout';

// Lazy-loaded pages — loaded on-demand for faster initial load
const Home = lazy(() => import('../pages/home'));
const Blog = lazy(() => import('../pages/blog'));
const About = lazy(() => import('../pages/about'));
const Enquire = lazy(() => import('../pages/enquire'));
const BookingEnquire = lazy(() => import('../pages/booking'));
const Departure = lazy(() => import('../pages/departure'));
const BookingNow = lazy(() => import('../pages/bookingNow'));
const Blogs = lazy(() => import('../pages/blogs'));
const Privacy = lazy(() => import('../pages/privacy'));
const Terms = lazy(() => import('../pages/terms'));
const CustomizeTour = lazy(() => import('../pages/customizeTour'));
const Explore2 = lazy(() => import('../pages/explore/index3'));
const ExploreAllContent = lazy(() => import('../pages/explore/ExploreAllContent'));
const TrekDetails = lazy(() => import('../pages/trek-details'));
const Login = lazy(() => import('../pages/Login/Login'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard'));
const Register = lazy(() => import('../pages/Register'));
const DiscoveryFeed = lazy(() => import('../pages/DiscoveryFeed'));
const Gallery = lazy(() => import('../pages/gallery'));
const Faqs = lazy(() => import('../pages/faqs'));
const NotFound = lazy(() => import('../pages/NotFound'));
const RevenueOps = lazy(() => import('../pages/RevenueOps'));
const MyTrip = lazy(() => import('../pages/MyTrip'));

function AppRouter() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route element={<PrivateLayout />}>
          <Route path="/my-trip" element={<MyTrip />} />
          <Route path="/ops" element={<ProtectedRoute requireAdmin><RevenueOps /></ProtectedRoute>} />
          <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
          <Route path="/register" element={<PageTransition><Register /></PageTransition>} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin={true}>
                <PageTransition>
                  <AdminDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute requireAdmin={true}>
                <PageTransition>
                  <AdminDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="/" element={<Layout />}>
          <Route index element={
            <PageTransition>
              <Home />
            </PageTransition>
          } />
          <Route path="/blogs" element={
            <PageTransition>
              <Blogs />
            </PageTransition>
          } />
          <Route path="/blog/:id" element={
            <PageTransition>
              <Blog />
            </PageTransition>
          } />
          <Route path="/about" element={
            <PageTransition>
              <About />
            </PageTransition>
          } />
          <Route path="/book-now/:id" element={
            <PageTransition>
              <BookingNow />
            </PageTransition>
          } />

          <Route path="/discover" element={
            <PageTransition>
              <DiscoveryFeed />
            </PageTransition>
          } />
          <Route path="/gallery" element={
            <PageTransition>
              <Gallery />
            </PageTransition>
          } />
          <Route path="/faqs" element={
            <PageTransition>
              <Faqs />
            </PageTransition>
          } />
        </Route>
        <Route path="/" element={<DocumentLayout />}>
          <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
          <Route path="/terms-condition" element={<PageTransition><Terms /></PageTransition>} />
        </Route>
        <Route path="/" element={<CloneLayout />}>
          <Route path="/explore" element={
            <PageTransition>
              <ExploreAllContent />
            </PageTransition>
          } />
          <Route path="/kailash-yatra" element={<PageTransition><ExploreAllContent category="kailash" /></PageTransition>} />
          <Route path="/nepal-tours" element={<PageTransition><ExploreAllContent category="nepal" /></PageTransition>} />
          <Route path="/pilgrimage-tours" element={<PageTransition><ExploreAllContent category="pilgrimage" /></PageTransition>} />
          <Route path="/trekking" element={<PageTransition><ExploreAllContent category="trekking" /></PageTransition>} />
          <Route path="/explore/:name" element={
            <PageTransition>
              <Explore2 />
            </PageTransition>
          } />
          <Route path="/trek/:trekId" element={
            <PageTransition>
              <TrekDetails />
            </PageTransition>
          } />
        </Route>
        <Route path="/" element={<MasterLayout />}>
          <Route path="/enquire/:id" element={
            <PageTransition>
              <Enquire />
            </PageTransition>
          } />
          <Route path="/departure" element={
            <PageTransition>
              <Departure />
            </PageTransition>
          } />
        </Route>
        {/* <Route path='/' element={<CloneLayout />}>
          <Route path='/contact' element={<BookingEnquire />} />
        </Route> */}
        <Route path="/" element={<EnquireLayout />}>
          <Route path="/contact" element={
            <PageTransition>
              <BookingEnquire />
            </PageTransition>
          } />
          <Route path="/customize" element={
            <PageTransition>
              <CustomizeTour />
            </PageTransition>
          } />
        </Route>

        {/* Legacy public URLs are redirected at the edge/SSR and kept here for SPA navigation. */}
        <Route path="/about-us/*" element={<Navigate replace to="/about" />} />
        <Route path="/activities/*" element={<Navigate replace to="/discover" />} />

        {/* 404 catch-all */}
        <Route path="*" element={
          <PageTransition>
            <NotFound />
          </PageTransition>
        } />
      </Routes>
    </AnimatePresence>
  );
}

export default AppRouter;
